/**
 * The support chat's calls (SUPPORT_CHAT_SPEC.md, customer API). Same client,
 * CSRF and idempotency rules as the vending calls in ../api.
 *
 * Reads never start a session: without one there is no case to read. Sending
 * may, because the first question can be the first thing a visitor does.
 */
import { ApiError, client, withSession } from "../api";
import type { Locale } from "../i18n";

export type SupportRole = "customer" | "assistant" | "agent";

export interface SupportMessage {
  seq: number;
  role: SupportRole;
  body: string;
  at: string;
}

export interface SupportCase {
  case_id: string;
  case_ref: string;
  status: "open" | "resolved";
  messages: SupportMessage[];
  turns_left: number;
  ai_available: boolean;
}

/** Display/triage hints sent with a message. Every field ≤ 200 characters. */
export interface ClientContext {
  route?: string;
  vend_session_id?: string;
  vend_state?: string;
  outcome_reason?: string;
  last_error_code?: string;
  last_trace_id?: string;
  app_build?: string;
  user_agent?: string;
}

export interface SendRequest {
  message: string;
  case_id?: string;
  locale: Locale;
  context?: ClientContext;
}

const BASE = "/support/chat";

function caseUrl(caseId: string): string {
  return `${BASE}/${encodeURIComponent(caseId)}`;
}

function opts(signal?: AbortSignal) {
  return signal ? { signal } : {};
}

/** The caller's latest open case; null when there is none or no session yet. */
export async function getOpenCase(signal?: AbortSignal): Promise<SupportCase | null> {
  try {
    const body = await client.get<{ case: SupportCase | null }>(BASE, opts(signal));
    return body.case;
  } catch (e) {
    if (e instanceof ApiError && e.status === 401) return null;
    throw e;
  }
}

export function getCase(caseId: string, signal?: AbortSignal): Promise<SupportCase> {
  return client.get<SupportCase>(caseUrl(caseId), opts(signal));
}

export function sendMessage(request: SendRequest, idempotencyKey: string): Promise<SupportCase> {
  return withSession(() => client.post<SupportCase>(BASE, request, { idempotencyKey }));
}

export function resolveCase(caseId: string, idempotencyKey: string): Promise<SupportCase> {
  return client.post<SupportCase>(`${caseUrl(caseId)}/resolve`, undefined, { idempotencyKey });
}

/** The support API is not deployed (or switched off): the widget stays hidden. */
export function isSupportUnavailable(e: unknown): boolean {
  return e instanceof ApiError && (e.status === 404 || e.status === 503);
}

/** The case is gone or belongs to another session (NotFoundOrNotOwned). */
export function isCaseGone(e: unknown): boolean {
  return e instanceof ApiError && (e.status === 404 || e.status === 401);
}

/** The catalogue key for a failed send or resolve. */
export function supportErrorKey(e: unknown): string {
  if (e instanceof ApiError) {
    if (e.code === "support_limit_reached") return "support.error.limit";
    if (e.code === "rate_limited" || e.status === 429) return "support.error.rateLimited";
    if (e.code === "support_case_closed") return "support.error.closed";
    if (e.code === "non_json_response" && e.status >= 502) return "support.error.network";
    return "support.error.generic";
  }
  return "support.error.network";
}

const ROLE_LABELS: Record<SupportRole, string> = {
  customer: "support.role.customer",
  assistant: "support.role.assistant",
  agent: "support.role.agent",
};

/** 「AIサポート」 / 「担当者」 / the customer's own (screen-reader) label. */
export function roleLabelKey(role: SupportRole): string {
  return ROLE_LABELS[role];
}

/** Seq of the newest assistant/agent message, -1 if none. */
export function latestReplySeq(c: SupportCase | null): number {
  let seq = -1;
  for (const m of c?.messages ?? []) {
    if (m.role !== "customer" && m.seq > seq) seq = m.seq;
  }
  return seq;
}
